/**
 * Type definitions for the unified section view endpoint.
 * POST /api/v1/sections/view returns polymorphic content based on section type.
 */

/**
 * @typedef {'MARKDOWN' | 'HTML' | 'TEXT' | 'AUDIO' | 'VIDEO' | 'QUIZ' | 'KNOWLEDGE_CHECK'} ContentType
 */

/**
 * @typedef {'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED'} ProgressStatus
 */

/**
 * @typedef {'IN_PROGRESS' | 'PASSED' | 'FAILED'} QuizStatus
 */

// ----- Content Type Definitions -----

/**
 * @typedef {Object} MarkdownContent
 * @property {'markdown'} type
 * @property {string} markdown
 */

/**
 * @typedef {Object} HtmlContent
 * @property {'html'} type
 * @property {string} html
 */

/**
 * @typedef {Object} TextContent
 * @property {'text'} type
 * @property {string} text
 */

/**
 * @typedef {Object} AudioContent
 * @property {'audio'} type
 * @property {string} url
 * @property {string | null} transcript
 * @property {number | null} durationSeconds
 */

/**
 * @typedef {Object} VideoContent
 * @property {'video'} type
 * @property {string} url
 * @property {string | null} transcript
 * @property {number | null} durationSeconds
 */

// ----- Quiz Types -----

/**
 * @typedef {Object} QuizMessage
 * @property {'user' | 'assistant'} role
 * @property {string} content
 * @property {string} timestamp - ISO-8601 format
 */

/**
 * @typedef {Object} QuizContent
 * @property {'quiz'} type
 * @property {string} sessionId
 * @property {string} quizId
 * @property {string} question
 * @property {string} difficulty - BEGINNER | INTERMEDIATE | ADVANCED
 * @property {QuizMessage[]} messageHistory
 * @property {QuizStatus} status
 * @property {number | null} score - 0.0 to 1.0 when completed
 * @property {boolean} isPassed
 */

// ----- Knowledge Check Types -----

/**
 * @typedef {Object} MCQuestion
 * @property {string} id
 * @property {string} question
 * @property {string[]} options - Array of answer choices (correctIndex NOT included)
 */

/**
 * @typedef {Object} PreviousAttempt
 * @property {number} score - Number correct
 * @property {number} total - Total questions
 * @property {boolean} passed
 * @property {number[]} answers - User's previous selections (indices)
 */

/**
 * @typedef {Object} KnowledgeCheckContent
 * @property {'knowledge_check'} type
 * @property {string} checkId
 * @property {MCQuestion[]} questions
 * @property {PreviousAttempt | null} previousAttempt
 */

// ----- Union Type -----

/**
 * @typedef {MarkdownContent | HtmlContent | TextContent | AudioContent | VideoContent | QuizContent | KnowledgeCheckContent} SectionContent
 */

// ----- Response Structure -----

/**
 * @typedef {Object} NavigationInfo
 * @property {string | null} previousSectionId
 * @property {string | null} nextSectionId
 */

/**
 * @typedef {Object} ProgressInfo
 * @property {ProgressStatus} status
 * @property {string | null} completedAt - ISO-8601 format
 */

/**
 * @typedef {Object} CourseInfo
 * @property {string} courseId
 * @property {string} courseTitle
 */

/**
 * @typedef {Object} LessonInfo
 * @property {string} lessonId
 * @property {string} title
 * @property {number} order
 */

/**
 * @typedef {Object} SectionInfo
 * @property {string} sectionId
 * @property {string} title
 * @property {number} order
 * @property {string} contentType
 */

/**
 * Main response from POST /api/v1/sections/view
 *
 * @typedef {Object} SectionViewResponse
 * @property {string} sectionId
 * @property {string} lessonId
 * @property {string} title
 * @property {ContentType} contentType
 * @property {SectionContent} content - Polymorphic based on contentType
 * @property {NavigationInfo} navigation
 * @property {ProgressInfo | null} progress
 * @property {CourseInfo} course
 * @property {LessonInfo} lesson
 * @property {SectionInfo} section
 */

// ----- Type Guards (runtime helpers) -----

/**
 * Check if content is quiz type
 * @param {SectionContent} content
 * @returns {content is QuizContent}
 */
export function isQuizContent(content) {
  return content?.type === 'quiz';
}

/**
 * Check if content is knowledge check type
 * @param {SectionContent} content
 * @returns {content is KnowledgeCheckContent}
 */
export function isKnowledgeCheckContent(content) {
  return content?.type === 'knowledge_check';
}

/**
 * Check if content is markdown type
 * @param {SectionContent} content
 * @returns {content is MarkdownContent}
 */
export function isMarkdownContent(content) {
  return content?.type === 'markdown';
}

/**
 * Check if content is HTML type
 * @param {SectionContent} content
 * @returns {content is HtmlContent}
 */
export function isHtmlContent(content) {
  return content?.type === 'html';
}

/**
 * Check if content is text type
 * @param {SectionContent} content
 * @returns {content is TextContent}
 */
export function isTextContent(content) {
  return content?.type === 'text';
}

/**
 * Check if content is audio type
 * @param {SectionContent} content
 * @returns {content is AudioContent}
 */
export function isAudioContent(content) {
  return content?.type === 'audio';
}

/**
 * Check if content is video type
 * @param {SectionContent} content
 * @returns {content is VideoContent}
 */
export function isVideoContent(content) {
  return content?.type === 'video';
}
